import { useTranslation } from 'react-i18next';
import { CheckExpandable } from '@/components/CheckExpandable';
import { FloatExpandableCard } from '@/components/FloatExpandableCard';
import { SectionHeader } from '@/components/SectionHeader';
import { Button, Card } from '@arco-design/web-react';
import { IconPlus } from '@arco-design/web-react/icon';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { GrayBox, InsideWhiteBox, ItemDescription, ItemTitle } from '../tools';
import { LifecycleItem, LifecycleManagePannels } from './lifecycle-manage-pannels';
import { LifecycleManageData, useLifecycleManageStore } from './useLifecycleManageStore';

export function LifecycleManage() {
  const { t } = useTranslation();

  const msg = {
    lifecycle_manage: t('components:lifecycle.management'),
    lifecycle_manage_desc: t(
      'components:set.actions.to.be.executed.after.container.startup.or.before.termination.for.environmental.monitoring.or.graceful.termination',
    ),
    poststart_action: t('components:post.start.action'),
    poststart_action_desc: t('components:actions.to.be.executed.after.container.startup'),
    prestop_action: t('components:pre.stop.action'),
    prestop_action_desc: t('components:actions.to.be.executed.before.container.termination'),
    add: t('common:add'),
  };

  const {
    isLifecycleManageSelected,
    toggleLifecycleManageSelected,
    postStartAction,
    preStopAction,
    updatePostStartAction,
    updatePreStopAction,
    removePostStartAction,
    removePreStopAction,
    validateLifecycleManage,
  } = useLifecycleManageStore();

  // 收集的数据
  const [currentPostStartAction, setCurrentPostStartAction] =
    useState<LifecycleManageData>(postStartAction);
  const [currentPreStopAction, setCurrentPreStopAction] =
    useState<LifecycleManageData>(preStopAction);

  // 后续可以优化成互斥打开
  const [openPostStartAction, setOpenPostStartAction] = useState(false);
  const [openPreStopAction, setOpenPreStopAction] = useState(false);

  // 是否打开配置面板
  const onTogglePostStartAction = (value?: boolean) => {
    if (value !== undefined) {
      setOpenPostStartAction(value);
    } else {
      setOpenPostStartAction((b) => !b);
    }
  };
  const onTogglePreStopAction = (value?: boolean) => {
    if (value !== undefined) {
      setOpenPreStopAction(value);
    } else {
      setOpenPreStopAction((b) => !b);
    }
  };

  // 收集当前更改的数据
  const handlePostStartActionChange = useCallback((data: LifecycleManageData) => {
    setCurrentPostStartAction(data);
  }, []);
  const handlePreStopActionChange = useCallback((data: LifecycleManageData) => {
    setCurrentPreStopAction(data);
  }, []);

  // 保存数据
  const onPostStartActionOk = async () => {
    const valid = await validateLifecycleManage();
    if (valid) {
      updatePostStartAction(currentPostStartAction);
      onTogglePostStartAction(false);
    }
  };
  const onPreStopActionOk = async () => {
    const valid = await validateLifecycleManage();
    if (valid) {
      updatePreStopAction(currentPreStopAction);
      onTogglePreStopAction(false);
    }
  };

  const Header = (
    <SectionHeader inline title={msg.lifecycle_manage} description={msg.lifecycle_manage_desc} />
  );
  return (
    <>
      <Card>
        <CheckExpandable
          label={Header}
          checked={isLifecycleManageSelected}
          onChange={toggleLifecycleManageSelected}
        >
          <Content>
            <GrayBox>
              <ItemTitle>{msg.poststart_action}</ItemTitle>
              <FloatExpandableCard
                content={
                  <LifecycleManagePannels
                    data={postStartAction}
                    onChange={handlePostStartActionChange}
                  />
                }
                isOpen={openPostStartAction}
                onOk={onPostStartActionOk}
                onCancel={() => onTogglePostStartAction(false)}
              >
                <InsideWhiteBox>
                  {!postStartAction ? (
                    <EmptyPlaceholder label={msg.add} onClick={onTogglePostStartAction} />
                  ) : (
                    <LifecycleItem
                      data={postStartAction}
                      onEditClick={() => {
                        onTogglePostStartAction(true);
                      }}
                      onDeleteClick={removePostStartAction}
                    />
                  )}
                </InsideWhiteBox>
              </FloatExpandableCard>
              <ItemDescription>{msg.poststart_action_desc}</ItemDescription>
            </GrayBox>
            <GrayBox>
              <ItemTitle>{msg.prestop_action}</ItemTitle>
              <FloatExpandableCard
                content={
                  <LifecycleManagePannels
                    data={preStopAction}
                    onChange={handlePreStopActionChange}
                  />
                }
                isOpen={openPreStopAction}
                onOk={onPreStopActionOk}
                onCancel={() => onTogglePreStopAction(false)}
              >
                <InsideWhiteBox>
                  {!preStopAction ? (
                    <EmptyPlaceholder label={msg.add} onClick={onTogglePreStopAction} />
                  ) : (
                    <LifecycleItem
                      data={preStopAction}
                      onEditClick={() => {
                        onTogglePreStopAction(true);
                      }}
                      onDeleteClick={removePreStopAction}
                    />
                  )}
                </InsideWhiteBox>
              </FloatExpandableCard>
              <ItemDescription>{msg.prestop_action_desc}</ItemDescription>
            </GrayBox>
          </Content>
        </CheckExpandable>
      </Card>
    </>
  );
}

function EmptyPlaceholder({ label, onClick }: { label?: string; onClick?: () => void }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <Button size="mini" type="primary" icon={<IconPlus />} onClick={onClick}>
        {label}
      </Button>
    </div>
  );
}

/** ============================ 样式 ==================================== */
const Content = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
